import { useState, useRef, useEffect } from 'react';
import { C } from '../../data/managerReviewData';
import VoiceButton from './VoiceButton';
import { structureAndCoach } from './reviewRefine';
import type { MultiSourceInsight } from './reviewRefine';

function hasRealRedirect(text: string): boolean {
  if (!text) return false;
  const t = text.trim();
  if (!t || t === '—' || t === '-' || t === 'None' || t === 'none' || t.length < 10) return false;
  return true;
}


interface ReviewTextareaProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  voice?: boolean;
  compact?: boolean;
  aiContext?: Record<string, unknown> | null;
  onRedirected?: (text: string) => void;
  onMultiSource?: (insights: MultiSourceInsight[]) => void;
  /** Increment to trigger auto-processing of the current value (e.g. after redirect appends text) */
  processSignal?: number;
}

export default function ReviewTextarea({
  value, onChange, placeholder, rows = 5,
  voice = false, compact = false, aiContext = null, onRedirected, onMultiSource,
  processSignal,
}: ReviewTextareaProps) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [compUpdating, setCompUpdating] = useState(false);
  const [sbirUpdating, setSbirUpdating] = useState(false);
  const [structured, setStructured] = useState('');
  const [coaching, setCoaching] = useState('');
  const [redirectLog, setRedirectLog] = useState<string[]>([]);
  const [wrongPerson, setWrongPerson] = useState('');
  const [inputCount, setInputCount] = useState(0);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [applied, setApplied] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [structuredOpen, setStructuredOpen] = useState(true);
  const [coachingOpen, setCoachingOpen] = useState(true);
  const [redirectedOpen, setRedirectedOpen] = useState(true);
  const lastProcessedRef = useRef('');
  const prevCompsRef = useRef('');
  const overlayText = recording ? '🎙 Listening…' : null;
  const displayValue = recording ? '' : value;

  const handleTextChange = (newVal: string) => {
    onChange(newVal);
    if (newVal !== lastProcessedRef.current && hasGenerated) { setNeedsUpdate(true); setApplied(false); setWrongPerson(''); }
    if (!newVal.trim()) setNeedsUpdate(false);
  };

  useEffect(() => {
    if (value === lastProcessedRef.current) setNeedsUpdate(false);
  }, [value]);

  const compsKey = aiContext
    ? JSON.stringify((aiContext.taggedCompetencies as string[]) || [])
    : '[]';

  useEffect(() => {
    if (prevCompsRef.current !== '' && compsKey !== prevCompsRef.current && value.trim() && hasGenerated && !processing && !compUpdating && !sbirUpdating) {
      // eslint-disable-next-line react-hooks/immutability
      void processCompChange();
    }
    prevCompsRef.current = compsKey;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compsKey]);

  useEffect(() => {
    if (!processSignal || !value.trim() || !aiContext) return;
    // eslint-disable-next-line react-hooks/immutability
    void processInput(value, structured || undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processSignal]);

  const handleRedirect = (text: string) => {
    if (hasRealRedirect(text)) {
      const clean = text.replace(/This has been moved.*$/gm, '').replace(/This sounds like constructive.*$/gm, '').trim();
      if (clean && clean.length > 5) {
        setRedirectLog(function(prev) { return prev.concat([clean]); });
        if (onRedirected) onRedirected(clean);
      }
    }
  };

  const handleResult = (sbir: SbirResult) => {
    setStructured(sbir.structured);
    setCoaching(sbir.coaching);
    handleRedirect(sbir.redirected);
    if (onMultiSource) onMultiSource(sbir.multiSource || []);
    const wp = sbir.wrongPerson || '';
    if (wp && wp !== '—' && wp.length > 5) { setWrongPerson(wp); } else { setWrongPerson(''); }
  };

  const processCompChange = async () => {
    if (!value.trim() || !aiContext) return;
    setCompUpdating(true);
    try {
      const sbir = await structureAndCoach(value, aiContext, structured || undefined);
      handleResult(sbir);
      lastProcessedRef.current = value;
      setHasGenerated(true);
      setApplied(false);
      setNeedsUpdate(false);
      setCoachingOpen(true);
    // eslint-disable-next-line no-empty
    } catch { }
    setCompUpdating(false);
  };

  const processInput = async (inputText: string, existingSbir?: string) => {
    if (!inputText.trim() || !aiContext) return;
    if (processing) return;
    setProcessing(true);
    setSbirUpdating(true);
    try {
      const sbir = await structureAndCoach(inputText, aiContext, existingSbir);
      handleResult(sbir);
      lastProcessedRef.current = value;
      setHasGenerated(true);
      setApplied(false);
      setNeedsUpdate(false);
      setStructuredOpen(true);
      setCoachingOpen(true);
      setRedirectedOpen(true);
    // eslint-disable-next-line no-empty
    } catch { }
    setSbirUpdating(false);
    setProcessing(false);
  };

  const handleStop = async (result: { segments: string[]; lang: 'en-US' | 'de-DE' }) => {
    setRecording(false);
    if (!result.segments.length) return;
    const rawText = result.segments.join(' ');
    const newValue = value ? value + '\n\n' + rawText : rawText;
    onChange(newValue);
    setInputCount(function(c) { return c + 1; });
    setNeedsUpdate(false);
    await processInput(rawText, structured || undefined);
  };

  const handleImprove = async () => {
    if (!value.trim()) return;
    await processInput(value, undefined);
  };

  const handleClear = () => {
    onChange('');
    setStructured('');
    setCoaching('');
    setRedirectLog([]);
    setWrongPerson('');
    setInputCount(0);
    setNeedsUpdate(false);
    setShowClearConfirm(false);
    setHasGenerated(false);
    setApplied(false);
    setSbirUpdating(false);
    lastProcessedRef.current = '';
    if (onMultiSource) onMultiSource([]);
  };

  const applyStructured = () => {
    if (!structured || applied) return;
    onChange(structured);
    lastProcessedRef.current = structured;
    setNeedsUpdate(false);
    setApplied(true);
  };

  const borderColor = recording ? C.red + '55' : C.border;
  const bgColor = recording ? '#fffafa' : C.inputBg;
  const hasContent = !!(structured || coaching || value.trim());
  const anyBusy = recording || processing || compUpdating || sbirUpdating;
  const showImprove = !!value.trim() && !anyBusy && (!hasGenerated || needsUpdate) && !!aiContext;

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <textarea value={displayValue}
          onChange={anyBusy ? () => undefined : (e) => handleTextChange(e.target.value)}
          placeholder={anyBusy ? '' : placeholder} rows={rows}
          style={{ width: '100%', padding: compact ? '8px 10px' : '10px 12px',
            border: '1px solid ' + borderColor, borderRadius: 6, background: bgColor,
            fontSize: compact ? 12 : 14, outline: 'none', color: C.text, resize: 'vertical',
            fontFamily: 'Inter', lineHeight: 1.6, boxSizing: 'border-box', transition: 'border-color 0.15s, background 0.15s' }} />
        {overlayText && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', borderRadius: 6 }}>
            <span style={{ fontSize: compact ? 11 : 13, fontWeight: 500, color: C.muted, fontStyle: 'italic', fontFamily: 'Inter' }}>
              {overlayText}
            </span>
          </div>
        )}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>

      {/* Action row — 🎙 Record · ✦ Refine · 🗑 Clear */}
      {!processing && !compUpdating && !sbirUpdating && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {voice && (
            <VoiceButton onStart={() => setRecording(true)} onStop={function(r) { void handleStop(r); }} onError={() => setRecording(false)} />
          )}
          {!recording && showImprove && (
            <button onClick={function() { void handleImprove(); }} style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              border: '1px solid ' + C.border, background: '#fff', color: C.muted,
              cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
            }}
              onMouseEnter={function(e) { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.color = C.blue; }}
              onMouseLeave={function(e) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
            >✦ Refine</button>
          )}
          {!recording && hasContent && (
            <button onClick={function() { setShowClearConfirm(true); }} style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              border: '1px solid ' + C.border, background: '#fff', color: C.muted,
              cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
            }}
              onMouseEnter={function(e) { e.currentTarget.style.borderColor = C.red; e.currentTarget.style.color = C.red; }}
              onMouseLeave={function(e) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
            >🗑 Clear</button>
          )}
        </div>
      )}

      {showClearConfirm && (
        <div style={{ marginTop: 6, padding: '12px 14px', borderRadius: 8, border: '1px solid ' + C.red + '44', background: '#fff5f5', fontFamily: 'Inter' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Are you sure you want to start over?</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>This will remove all feedback and cannot be undone.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={function() { setShowClearConfirm(false); }} style={{ padding: '5px 14px', borderRadius: 5, border: '1px solid ' + C.border, background: '#fff', fontSize: 11, fontWeight: 600, color: C.text, cursor: 'pointer', fontFamily: 'Inter' }}>Cancel</button>
            <button onClick={handleClear} style={{ padding: '5px 14px', borderRadius: 5, border: '1px solid ' + C.red, background: C.red, fontSize: 11, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter' }}>Yes, start over</button>
          </div>
        </div>
      )}

      {wrongPerson && (
        <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 6, border: '1px solid #F09595', background: '#FCEBEB', fontSize: compact ? 11 : 12, lineHeight: 1.6, fontFamily: 'Inter', color: '#791F1F' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, color: '#A32D2D' }}>⛔ Wrong employee detected</div>
          {wrongPerson}
        </div>
      )}

      {redirectLog.length > 0 && (
        <CollapsibleSection open={redirectedOpen} onToggle={function() { setRedirectedOpen(function(o) { return !o; }); }}
          label={'⚠️ Moved to 🔧 What could have gone better? (' + redirectLog.length + ')'}
          borderColor="#f59e0b44" bgColor="#fffbeb" textColor="#92400e" compact={compact}>
          {redirectLog.map(function(item, i) {
            return (<div key={i} style={{ marginBottom: i < redirectLog.length - 1 ? 8 : 0, paddingBottom: i < redirectLog.length - 1 ? 8 : 0, borderBottom: i < redirectLog.length - 1 ? '1px solid #f59e0b22' : 'none' }}>{item}</div>);
          })}
        </CollapsibleSection>
      )}

      {(structured || sbirUpdating) && (
        <CollapsibleSection open={structuredOpen} onToggle={function() { setStructuredOpen(function(o) { return !o; }); }}
          label={sbirUpdating ? '📋 SBIR Structured Feedback — updating…' : '📋 SBIR Structured Feedback' + (inputCount > 1 ? ' (' + inputCount + ' inputs merged)' : '')}
          borderColor="#10b98133" bgColor="#f0fdf4" textColor="#166534" compact={compact}
          action={!sbirUpdating ? (
            <button onClick={applied ? undefined : applyStructured} style={{
              padding: '3px 8px', borderRadius: 4, border: '1px solid ' + (applied ? '#d1d5db' : '#10b981'),
              background: applied ? '#f3f4f6' : '#fff', fontSize: 10, fontWeight: 600,
              color: applied ? '#9ca3af' : '#166534', cursor: applied ? 'default' : 'pointer',
              fontFamily: 'Inter', transition: 'background 0.1s', opacity: applied ? 0.6 : 1,
            }}
              onMouseEnter={function(e) { if (!applied) e.currentTarget.style.background = '#d1fae5'; }}
              onMouseLeave={function(e) { if (!applied) e.currentTarget.style.background = '#fff'; }}
            >{applied ? '✅ Applied' : '✅ Use this'}</button>
          ) : undefined}>
          {sbirUpdating ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontStyle: 'italic', opacity: 0.7 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #166534', borderTopColor: 'transparent', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
              Updating structured feedback…
            </span>
          ) : <div style={{ whiteSpace: 'pre-wrap' }}>{structured}</div>}
        </CollapsibleSection>
      )}

      {(coaching || compUpdating) && (
        <CollapsibleSection open={coachingOpen} onToggle={function() { setCoachingOpen(function(o) { return !o; }); }}
          label={compUpdating ? '💡 Coach — updating…' : '💡 Coach'}
          borderColor={C.blue + '33'} bgColor="#f0f7ff" textColor={C.blue} compact={compact}>
          {compUpdating ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontStyle: 'italic', opacity: 0.7 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid ' + C.blue, borderTopColor: 'transparent', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
              Updating coaching…
            </span>
          ) : (<div style={{ whiteSpace: 'pre-wrap' }}>{coaching}</div>)}
        </CollapsibleSection>
      )}
    </div>
  );
}

type SbirResult = { structured: string; coaching: string; redirected: string; multiSource: MultiSourceInsight[]; wrongPerson: string };

function CollapsibleSection({ open, onToggle, label, borderColor, bgColor, textColor, compact, action, children }: {
  open: boolean; onToggle: () => void; label: string; borderColor: string; bgColor: string; textColor: string; compact?: boolean; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div style={{ marginTop: 6, borderRadius: 6, border: '1px solid ' + borderColor, background: bgColor, fontFamily: 'Inter', overflow: 'hidden' }}>
      <div onClick={onToggle} style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: textColor, opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: 9 }}>▶</span>
          {label}
        </span>
        {action && open && <span onClick={function(e) { e.stopPropagation(); }}>{action}</span>}
      </div>
      {open && (
        <div style={{ padding: '0 12px 10px 12px', fontSize: compact ? 11 : 12, lineHeight: 1.7, color: textColor }}>{children}</div>
      )}
    </div>
  );
}
